'use client';

import { createClient } from '@/lib/supabase/client';

export type NotificationType = 'booking_confirmation' | 'loyalty_reward' | 'membership_invitation';

export interface AppNotification {
  id: string;
  visitor_email: string;
  type: NotificationType;
  title: string;
  message: string;
  metadata: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

export const notificationService = {
  /**
   * Insert a notification for a visitor by email.
   * Called alongside email delivery — non-blocking, never throws.
   */
  async create(params: {
    visitorEmail: string;
    type: NotificationType;
    title: string;
    message: string;
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    try {
      const supabase = createClient();
      await supabase.from('notifications').insert({
        visitor_email: params.visitorEmail,
        type: params.type,
        title: params.title,
        message: params.message,
        metadata: params.metadata ?? {},
      });
    } catch {
      // Non-blocking — silently fail so it never breaks the main flow
    }
  },

  /** Fetch all notifications for the logged-in visitor (newest first). */
  async getForUser(email: string): Promise<AppNotification[]> {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('visitor_email', email)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) return [];
      return (data ?? []) as AppNotification[];
    } catch {
      return [];
    }
  },

  /** Mark a single notification as read. */
  async markRead(id: string): Promise<void> {
    try {
      const supabase = createClient();
      await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    } catch {
      // silent
    }
  },

  /** Mark all notifications for a visitor as read. */
  async markAllRead(email: string): Promise<void> {
    try {
      const supabase = createClient();
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('visitor_email', email)
        .eq('is_read', false);
    } catch {
      // silent
    }
  },
};
